import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock, Sparkles, Check, LogIn } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { PLAN_PRICES, TRIAL_DAYS } from "@/context/BillingContext";

export const FEATURE_KEYS = ["workspace", "simulator", "lab", "scenarios", "quizzes", "certificates", "team", "feature"];

// Shared lock copy: one component, CTA differs for logged-out vs free users.
export function UpgradeCta({ feature = "feature", compact = false, onDone }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const go = (to) => { onDone?.(); navigate(to); };
  return (
    <div className={compact ? "flex flex-col sm:flex-row gap-2" : "flex flex-col sm:flex-row gap-3 justify-center"} data-testid="upgrade-cta">
      <Button data-testid="upgrade-see-plans" onClick={() => go(`/pricing?from=${feature}`)} className="bg-primary hover:bg-primary/90 text-white rounded-full px-6">
        <Sparkles size={16} className="mr-2" /> {user ? t("billing.cta.startTrial", { days: TRIAL_DAYS }) : t("billing.cta.seePlans")}
      </Button>
      {!user && (
        <Button variant="secondary" data-testid="upgrade-sign-in" onClick={() => go(`/login?next=${encodeURIComponent(window.location.pathname)}`)} className="rounded-full px-6 bg-secondary hover:bg-slate-700 border border-white/10">
          <LogIn size={16} className="mr-2" /> {t("billing.cta.signIn")}
        </Button>
      )}
    </div>
  );
}

export function PlanHighlights({ feature }) {
  const { t } = useTranslation();
  const items = [t(`billing.features.${feature}`, t("billing.features.feature")), t("billing.highlights.all"), t("billing.highlights.certs"), t("billing.highlights.cancel")];
  return (
    <ul className="space-y-1.5 text-sm text-slate-300 text-left" data-testid="upgrade-highlights">
      {items.map((x, i) => <li key={i} className="flex items-start gap-2"><Check size={15} className="text-green-400 mt-0.5 shrink-0" /> <span>{x}</span></li>)}
    </ul>
  );
}

// Global dialog, opened anywhere via window event "fab:upgrade" (see openUpgrade()).
export default function UpgradeDialog() {
  const { t } = useTranslation();
  const [feature, setFeature] = useState(null);
  useEffect(() => {
    const h = (e) => setFeature(e.detail?.feature || "feature");
    window.addEventListener("fab:upgrade", h);
    return () => window.removeEventListener("fab:upgrade", h);
  }, []);
  return (
    <Dialog open={!!feature} onOpenChange={(o) => !o && setFeature(null)}>
      <DialogContent className="bg-[#0B132B] border-white/10 text-white max-w-md" data-testid="upgrade-dialog">
        <DialogHeader>
          <div className="mx-auto grid place-items-center w-12 h-12 rounded-full bg-primary/15 text-primary mb-2"><Lock size={22} /></div>
          <DialogTitle className="font-heading text-center text-xl">{t("billing.lock.title")}</DialogTitle>
          <DialogDescription className="text-center text-slate-400">{t("billing.lock.body", { price: PLAN_PRICES.individual, days: TRIAL_DAYS })}</DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-white/10 bg-card p-4"><PlanHighlights feature={feature || "feature"} /></div>
        <UpgradeCta feature={feature || "feature"} compact onDone={() => setFeature(null)} />
      </DialogContent>
    </Dialog>
  );
}
